import { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { FinancialClass, TransactionCategory, TransactionType } from '@inversor-hub/types';
import { transactionCategoryLabels } from '@/features/investment-transactions/constants';
import { useListInvestmentTransactions } from '@/lib/api/investment-transactions';
import { useUpdateLineFinancialClass } from '@/lib/api/investment-financials';
import { formatCurrency, formatDate } from '@/utils/misc';
import { allowedFinancialClasses, financialClassHelp, financialClassLabels } from './metrics-copy';

interface ClassifyLinesDialogProps {
  open: boolean;
  investmentId: string;
  onClose: () => void;
}

interface PendingLine {
  transactionId: string;
  transactionType: TransactionType;
  concept: string;
  date: string;
  lineId: string;
  category: TransactionCategory;
  amount: number;
}

export const ClassifyLinesDialog = ({ open, investmentId, onClose }: ClassifyLinesDialogProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: transactions, isLoading } = useListInvestmentTransactions(
    open ? investmentId : undefined
  );
  const updateClass = useUpdateLineFinancialClass(investmentId);

  const pendingLines = useMemo<PendingLine[]>(
    () =>
      (transactions ?? []).flatMap((transaction) =>
        transaction.lines
          .filter((line) => line.financialClass === 'UNCLASSIFIED')
          .map((line) => ({
            transactionId: transaction.id,
            transactionType: transaction.type,
            concept: transaction.concept || 'Sin concepto',
            date: transaction.date,
            lineId: line.id,
            category: line.category,
            amount: line.amount,
          }))
      ),
    [transactions]
  );

  const handleChange = async (line: PendingLine, financialClass: FinancialClass) => {
    try {
      await updateClass.mutateAsync({
        transactionId: line.transactionId,
        lineId: line.lineId,
        financialClass,
      });
      enqueueSnackbar('Movimiento clasificado', { variant: 'success' });
    } catch (_error) {
      enqueueSnackbar('No se pudo clasificar el movimiento', { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Clasificar movimientos</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2.5 }}>
          La categoría fiscal dice cómo declarar un movimiento; la clasificación financiera dice
          dónde entra en la rentabilidad. Mientras un movimiento esté sin clasificar, no suma ni
          resta en ninguna métrica.
        </Alert>

        {isLoading ? (
          <Skeleton variant="rectangular" height={200} />
        ) : pendingLines.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No queda ningún movimiento sin clasificar.
          </Typography>
        ) : (
          <Stack divider={<Divider />} spacing={0}>
            {pendingLines.map((line) => (
              <Box
                key={line.lineId}
                sx={{
                  py: 2,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {line.concept}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(line.date)} · {transactionCategoryLabels[line.category]} ·{' '}
                    {formatCurrency(line.amount)}
                  </Typography>
                </Box>
                <Select
                  size="small"
                  displayEmpty
                  value=""
                  onChange={(event) => handleChange(line, event.target.value as FinancialClass)}
                  disabled={updateClass.isPending}
                  sx={{ minWidth: { xs: '100%', sm: 260 } }}
                  inputProps={{ 'aria-label': `Clasificar ${line.concept}` }}
                >
                  <MenuItem value="" disabled>
                    Elige una clasificación
                  </MenuItem>
                  {allowedFinancialClasses(line.transactionType).map((financialClass) => (
                    <MenuItem key={financialClass} value={financialClass}>
                      <Box>
                        <Typography variant="body2">
                          {financialClassLabels[financialClass]}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {financialClassHelp[financialClass]}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Hecho
        </Button>
      </DialogActions>
    </Dialog>
  );
};
